import { Link } from "@tanstack/react-router";
import { CalendarDays, Clock } from "lucide-react";

import { formatDate, readingMinutes } from "@/lib/blog";
import type { BlogLocale, BlogPost } from "@/lib/blog";
import { frameImageProps, readFrame } from "@/lib/image-frame";
import type { NewsletterSection } from "@/lib/editorial";
import { MarkdownBody } from "@/components/site/Markdown";
import { RemoteImage } from "@/components/site/RemoteImage";

const T: Record<BlogLocale, { empty: string; read: string; min: string; back: string }> = {
  sv: { empty: "Snart publicerar vi de första inläggen här.", read: "Läs inlägget", min: "min läsning", back: "Alla inlägg" },
  en: { empty: "The first posts will be published here soon.", read: "Read the post", min: "min read", back: "All posts" },
  es: { empty: "Pronto publicaremos las primeras entradas aquí.", read: "Leer la entrada", min: "min de lectura", back: "Todas las entradas" },
};

function postHref(locale: BlogLocale, slug: string) {
  if (locale === "sv") return `/blogg/${slug}`;
  if (locale === "es") return `/es/blog/${slug}`;
  return `/en/blog/${slug}`;
}

export function BlogList({ posts, locale }: { posts: BlogPost[]; locale: BlogLocale }) {
  const t = T[locale];
  if (posts.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16">
        <p className="rounded-xl border border-border bg-card px-5 py-8 text-center text-sm text-muted-foreground">
          {t.empty}
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-5 py-16">
      <div className="grid gap-6 sm:grid-cols-2">
        {posts.map((post) => (
          <article
            key={post.id}
            className="group relative flex flex-col overflow-hidden rounded-xl border border-border bg-card transition-colors hover:border-primary/40"
          >
            {post.coverImageUrl ? (
              <RemoteImage
                src={post.coverImageUrl}
                alt={post.coverImageAlt || post.title}
                renderWidth={800}
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px"
                className="w-full"
                {...frameImageProps(post.coverFrame, "16 / 10")}
              />
            ) : null}

            <div className="flex flex-1 flex-col p-5">
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                {post.category ? (
                  <span className="rounded-full bg-primary/10 px-2.5 py-0.5 font-medium text-primary">
                    {post.category}
                  </span>
                ) : null}
                <span className="inline-flex items-center gap-1">
                  <CalendarDays className="size-3.5" />
                  {formatDate(post.publishedAt, locale)}
                </span>
              </div>
              <h2 className="mt-3 font-display text-xl font-semibold leading-snug">
                <Link to={postHref(locale, post.slug)} className="after:absolute after:inset-0">
                  {post.title}
                </Link>
              </h2>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground/80">{post.excerpt}</p>
              <span className="mt-4 text-sm font-medium text-primary group-hover:underline">
                {t.read} →
              </span>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

/** Omvandlar en sektions bildinställningar till props för RemoteImage. */
function sectionImageProps(section: NewsletterSection, fallback: string) {
  return frameImageProps(readFrame(section as unknown as Record<string, unknown>), fallback);
}

/** Samma sak för inläggets omslagsbild. */
function coverImageProps(post: BlogPost, fallback: string) {
  return frameImageProps(post.coverFrame, fallback);
}


/** Valfria innehållsblock från sektionsbyggaren. */
function ArticleSections({ sections, fallbackAlt }: { sections: NewsletterSection[]; fallbackAlt: string }) {
  return (
    <div className="mt-10 space-y-8">
      {sections.map((s) => {
        if (s.type === "divider") return <hr key={s.id} className="border-border" />;
        if (s.type === "heading")
          return (
            <h2 key={s.id} className="font-display text-2xl font-semibold">
              {s.heading}
            </h2>
          );
        if (s.type === "text")
          return (
            <MarkdownBody key={s.id} content={s.text ?? ""} />
          );
        if (s.type === "image")
          return (
            <figure key={s.id} className={s.width === "text" ? "mx-auto max-w-xl" : undefined}>
              {s.src ? <RemoteImage src={s.src} alt={s.alt?.trim() || s.caption?.trim() || s.heading?.trim() || fallbackAlt} renderWidth={960} sizes="(max-width: 768px) 100vw, 768px" className="w-full rounded-xl border border-border" {...sectionImageProps(s, "16 / 9")} /> : null}
              {s.caption ? (
                <figcaption className="mt-2 text-xs text-muted-foreground">{s.caption}</figcaption>
              ) : null}
            </figure>
          );
        if (s.type === "imageText")
          return (
            <div
              key={s.id}
              className={`grid gap-6 sm:grid-cols-2 sm:items-center ${s.align === "right" ? "sm:[&>figure]:order-2" : ""}`}
            >
              <figure>
                {s.src ? <RemoteImage src={s.src} alt={s.alt?.trim() || s.caption?.trim() || s.heading?.trim() || fallbackAlt} renderWidth={960} sizes="(max-width: 768px) 100vw, 768px" className="w-full rounded-xl border border-border" {...sectionImageProps(s, "16 / 9")} /> : null}
              </figure>
              <div className="space-y-2">
                {s.heading ? <h3 className="font-display text-lg font-semibold">{s.heading}</h3> : null}
                <MarkdownBody content={s.text ?? ""} />
              </div>
            </div>
          );
        if (s.type === "textOverImage")
          return (
            <div key={s.id} className="relative overflow-hidden rounded-xl border border-border">
              {s.src ? <RemoteImage src={s.src} alt={s.alt?.trim() || s.heading?.trim() || fallbackAlt} renderWidth={960} sizes="(max-width: 768px) 100vw, 768px" className="w-full" {...sectionImageProps(s, "21 / 9")} /> : null}
              <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-photo-shade/75 to-transparent p-6">
                {s.heading ? (
                  <h3 className="font-display text-xl font-semibold text-photo-text">{s.heading}</h3>
                ) : null}
                 {s.text ? <MarkdownBody content={s.text} className="mt-1 text-sm text-photo-text/90" /> : null}
              </div>
            </div>
          );
        if (s.type === "button")
          return (
            <p key={s.id}>
              <a
                href={s.url}
                className="inline-flex rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground transition hover:opacity-90"
              >
                {s.label}
              </a>
            </p>
          );
        return null;
      })}
    </div>
  );
}

export function BlogArticle({ post, locale }: { post: BlogPost; locale: BlogLocale }) {
  const t = T[locale];
  return (
    <article className="mx-auto max-w-3xl px-5 py-12 sm:py-16">
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        {post.category ? (
          <span className="rounded-full bg-primary/10 px-2.5 py-0.5 font-medium text-primary">
            {post.category}
          </span>
        ) : null}
        <span className="inline-flex items-center gap-1">
          <CalendarDays className="size-3.5" />
          {formatDate(post.publishedAt, locale)}
        </span>
        <span className="inline-flex items-center gap-1">
          <Clock className="size-3.5" />
          {readingMinutes(post.content)} {t.min}
        </span>
      </div>

      <h1 className="mt-4 font-display text-3xl font-semibold leading-tight sm:text-4xl">{post.title}</h1>
      {post.excerpt ? (
        <p className="mt-4 text-lg leading-relaxed text-foreground/80">{post.excerpt}</p>
      ) : null}

      {post.coverImageUrl ? (
        <RemoteImage
          src={post.coverImageUrl}
          alt={post.coverImageAlt || post.title}
          priority
          renderWidth={1280}
          sizes="(max-width: 768px) 100vw, 768px"
          className="mt-8 w-full rounded-xl border border-border"
          {...coverImageProps(post, "16 / 9")}
        />
      ) : null}

      <MarkdownBody content={post.content} className="mt-8" />

      {post.sections.length > 0 ? <ArticleSections sections={post.sections} fallbackAlt={post.title} /> : null}

      {post.authorName ? (
        <p className="mt-10 text-sm text-muted-foreground">{post.authorName}</p>
      ) : null}

      <div className="mt-10 border-t border-border pt-6">
        <Link
          to={locale === "sv" ? "/blogg" : locale === "es" ? "/es/blog" : "/en/blog"}
          className="text-sm font-medium text-primary hover:underline"
        >
          ← {t.back}
        </Link>
      </div>
    </article>
  );
}
